import { useCallback, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  changeReimbursementCareCreditNumber,
  changeReimbursementExpirationDate,
  changeReimbursementFirstName,
  changeReimbursementLastName,
} from '../../../../../actions';
import { isValidMOD } from '../../../../../services/utils';

const CARE_CREDIT_PCLL = '601918';
const CARE_CREDIT_MC = '524306';
const CARE_CREDIT_MC_2 = '521344';
const CARE_CREDIT_DISCOVER = '650178';

// Valid Care credit cards shall start with:
const START_CARD_CREDIT_VALID = [
  CARE_CREDIT_PCLL,
  CARE_CREDIT_MC,
  CARE_CREDIT_MC_2,
  CARE_CREDIT_DISCOVER,
];

export const useCareCreditCardContent = ({ onCancel = () => { } }) => {
  const { t } = useTranslation('myAccount');
  const dispatch = useDispatch();
  const store = useSelector(({
    billingPayments,
    personalInformation,
  }) => ({ billingPayments, personalInformation }), shallowEqual);
  const { billingPayments } = store;
  const {
    reimbursementData: { careCredit },
  } = billingPayments;
  const [firstNameError, setFirstNameError] = useState('');
  const [lastNameError, setLastNameError] = useState('');
  const [cardNumberError, setCardNumberError] = useState('');
  const [expDateError, setExpDateError] = useState('');

  const onFirstNameChange = useCallback((event) => {
    const { target } = event;
    dispatch(changeReimbursementFirstName(target.value));
    setFirstNameError('');
  }, [dispatch]);

  const onFirstNameBlur = useCallback(() => {
    if (!careCredit.firstName.trim().length) {
      setFirstNameError(
        t('reimbursementSection.careCredit.nameHolderRequired'),
      );
    }
  }, [careCredit, t]);

  const onLastNameChange = useCallback((event) => {
    const { target } = event;
    dispatch(changeReimbursementLastName(target.value));
    setLastNameError('');
  }, [dispatch]);

  const onLastNameBlur = useCallback(() => {
    if (!careCredit.lastName.trim().length) {
      setLastNameError(t('reimbursementSection.careCredit.nameHolderRequired'));
    }
  }, [careCredit, t]);

  const onCareCreditNumberChange = useCallback((event) => {
    const { target } = event;
    const cleanedValue = target.value.replace(/\D/g, '');
    dispatch(changeReimbursementCareCreditNumber(cleanedValue));
    setCardNumberError('');
  }, [dispatch]);

  const cardNumber = careCredit.cardNumber || '';

  const onCareCreditNumberBlur = useCallback(() => {
    if (!cardNumber.trim().length) {
      setCardNumberError(
        t('reimbursementSection.careCredit.creditCardRequired'),
      );
      return;
    }

    // range of the care credit card number shall be from 15 to 19 digits
    if (!(/\d{15,19}/.test(cardNumber))) {
      setCardNumberError(t('reimbursementSection.careCredit.invalidCard'));
      return;
    }

    const firstNumber = cardNumber.substring(0, 6);
    const isStartCardValid = START_CARD_CREDIT_VALID
      .some((item) => item === firstNumber);

    const modValidation = isValidMOD(cardNumber);

    if (!isStartCardValid || !modValidation) {
      setCardNumberError(t('reimbursementSection.careCredit.invalidCard'));
    }
  }, [careCredit, t]);

  const validateExpDate = useCallback((expDate) => {
    if (!expDate?.length) {
      return;
    }

    const expirationDateRegex = /^(0[1-9]|1[0-2])\/?([0-9]{4})$/;
    const validStringFormat = expirationDateRegex.test(expDate)
      && expDate.includes('/');
    const stringMatched = expDate.match(expirationDateRegex);

    if (validStringFormat && stringMatched) {
      // const [month, year] = expDate.split('/');
      // eslint-disable-next-line no-unused-vars
      const [_, month, year] = stringMatched;
      const today = new Date();
      const expDateFormatted = new Date();
      expDateFormatted.setFullYear(year, parseInt(month, 10) - 1);

      const message = expDateFormatted < today
        ? t('reimbursementSection.careCredit.expired') : '';

      setExpDateError(message);
      return;
    }

    setExpDateError(t('reimbursementSection.careCredit.invalidDate'));
  }, [t]);

  const formatExpirationDate = useCallback((newExpDate) => {
    const currentExpDate = careCredit.expDate;

    if (!currentExpDate) {
      return newExpDate;
    }

    if (currentExpDate.includes('/') && !newExpDate.includes('/')) {
      return newExpDate;
    }

    if (newExpDate.length === 2 && !currentExpDate.includes('/')) {
      return `${newExpDate}/`;
    }

    return newExpDate;
  }, [careCredit.expDate]);

  const onExpirationDateChanged = useCallback((event) => {
    const { target: { value } } = event;
    const newExpDate = formatExpirationDate(value);
    dispatch(changeReimbursementExpirationDate(newExpDate));

    if (newExpDate && expDateError) {
      setExpDateError('');
    }

    validateExpDate(newExpDate);
  }, [dispatch, expDateError, careCredit.expDate]);

  const onExpirationDateBlur = useCallback(() => {
    validateExpDate(careCredit.expDate);
  }, [careCredit.expDate, t, validateExpDate]);

  const onCancelCareCardDetails = useCallback(() => {
    // clean up
    dispatch(changeReimbursementCareCreditNumber(''));
    dispatch(changeReimbursementExpirationDate(null));
    dispatch(changeReimbursementFirstName(''));
    dispatch(changeReimbursementLastName(''));

    onCancel();
  }, [dispatch, onCancel]);

  return {
    cardNumberError,
    careCredit,
    expDateError,
    firstNameError,
    lastNameError,
    onCancelCareCardDetails,
    onCareCreditNumberBlur,
    onCareCreditNumberChange,
    onExpirationDateBlur,
    onExpirationDateChanged,
    onFirstNameBlur,
    onFirstNameChange,
    onLastNameBlur,
    onLastNameChange,
  };
};
